import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileSaveIcon from '../../../../../assets/svg/file_save.svg?react';
import styles from './DocumentListVault.module.scss';
import { v4 as uuidv4 } from 'uuid';
import { Document, DocumentListVaultProps } from './types';
import { Document as StrictDocument } from '../../../document-vault/components/document-list-vault/types';
import FileTypeDownloadModal from '../../../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../../../common/file-type-download-modal/useFileTypeDownload';
import { DocumentsEditButton } from '../../../../common/document-edit-button/DocumentsEditButton';
import MessageModal from '../../../../common/message-modal/MessageModal';
import { useDeleteMessageModal } from '../../hooks/useDeleteMessageModal';
import { useDocumentsFunctions } from '../../hooks/useDocumentsFunctions';
import { DocumentsListRow } from './DocumentsListRow';

import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import {
  hideOverlay,
  showOverlay,
} from '../../../../slices/show-progress-overlay/showProgressOverlay';
import InfoIcon from '@mui/icons-material/Info';
import { Helpers } from '../../../../common/helpers/helpers';
import { NWClient } from '../../../../client/NWClient';
import { DocumentHtmlDataModel } from '../../../../models/document.html.data.model';
import { toast } from 'react-toastify';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { useErrorMessage } from '../../../../common/hooks/useErrorMessage';
import { ROUTES } from '../../../../common/constants/routes';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';
import { useLocation } from 'react-router-dom';

const DocumentListVault = ({ documents }: DocumentListVaultProps) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  /*const userIsEmployee = Boolean(
    useAppSelector(selectUserRoleByGroupName(GROUPS.COMPANY_EMPLOYEE))
  );*/
  const { navigateToDocumentPageTab } = useDocumentsFunctions();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const path = location.pathname;
  const disAllowedExtensions = ['jpg', 'jpeg', 'heic', 'png'];
  const {
    setModalOpen: setDeleteMessageModalOpen,
    modalOpen: deleteMessageModalOpen,
    pendingStatus: documentDeletePendingStatus,
    setDocumentToDelete,
    handleDeleteDocument,
  } = useDeleteMessageModal();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    setDocument: setDocumentForDownloading,
    document: documentForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleDownloadContract,
    downloadDocumentUploadItem,
    handleDownloadHTML,
  } = useFileTypeDownload();
  const { redirectToDocumenso } = useDocumensoRedirect();
  const [pdfSaveDialogOpen, setPdfSaveDialogOpen] = useState(false);

  const renderActionButtons = (doc: Document) => (
    <Box className={styles.actions_container}>
      {doc?.is_flattened && (
        <Tooltip title={t('messages.flattenedPDFMessage')} arrow placement='top'>
          <InfoIcon
            color='primary'
            fontSize='small'
            style={{ marginRight: '8px' }}
            className={'info-icon'}
          />
        </Tooltip>
      )}
      {/*!disAllowedExtensions.some((ext) => doc.name?.toLowerCase().endsWith(`.${ext}`)) &&
        !doc.is_flattened && (
          <Tooltip
            title={userIsEmployee ? t('messages.contactAdministrator') : t('buttons.edit')}
            placement='top'
            arrow
          >
            <IconButton
              id='documentsKeyDatesDocumentListVaultEditIconBtn'
              size='small'
              disabled={userIsEmployee}
              onClick={
                userIsEmployee
                  ? undefined
                  : () => navigateToDocumentPageTab(doc, 1, false, false, true)
              }
              className={styles.action_button}
            >
              <BrushIcon id='documentsKeyDatesDocumentListVaultEditIconBtnBrushIcon' />
            </IconButton>
          </Tooltip>
        )*/}
      <Tooltip title={t('buttons.download')} placement='top' arrow>
        <IconButton
          id='documentsKeyDatesDocumentListVaultDownloadIconBtn'
          size='small'
          onClick={() => {
            if (doc.signing_doc_id /*&& doc.status === 'COMPLETED'*/) {
              redirectToDocumenso(
                `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${path}&activeTab=${doc.category}`
              ).catch();
            } else {
              if ('contract_generation' in doc) {
                setDocumentForDownloading(doc as unknown as StrictDocument);
                setFileNameForDownloading(doc.name ?? '');
                setFileTypeModalOpen(true);
              } else {
                /*if ((doc.document_upload_html?.length ?? 0) > 0) {
                  setDocumentForDownloading(doc as unknown as StrictDocument);
                  setFileTypeModalOpen(true);
                } else {*/
                downloadDocumentUploadItem(doc.id, doc.name ?? '');
                //}
              }
            }
          }}
          className={styles.action_button}
        >
          <FileSaveIcon id='documentsKeyDatesDocumentListVaultDownloadIconBtnFileSaveIcon' />
        </IconButton>
      </Tooltip>
      <DocumentsEditButton
        document={doc as unknown as StrictDocument}
        isImage={disAllowedExtensions.some((ext) => doc.name?.toLowerCase().endsWith(`.${ext}`))}
        getDocumentSummaryFn={
          doc?.signing_doc_id ? undefined : () => navigateToDocumentPageTab(doc, 1, false, true)
        }
        createTemplateFn={
          undefined /*
          !doc.contract_generation && !userIsEmployee && !doc.is_flattened
            ? () => navigateToDocumentPageTab(doc, 1, true)
            : undefined*/
        }
        duplicateDocumentFn={!('contract_generation' in doc) && !doc?.signing_doc_id}
        deleteDocumentFn={() => {
          setDocumentToDelete(doc);
          setDeleteMessageModalOpen(true);
        }}
        documentLabelsFn={doc?.signing_doc_id ? undefined : () => navigateToDocumentPageTab(doc, 1)}
      />
    </Box>
  );

  return (
    <>
      <TableContainer
        component={Paper}
        // Key Dates block layout (same as the Document Vault): no header row; the block draws the
        // border and the rounded corners, `.table_container_embedded` sets the max height.
        className={`${styles.table_container} ${styles.table_container_compact} ${
          styles.table_container_embedded
        }`}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'auto',
        }}
      >
        <Table className={styles.compact_table} aria-label={t('headers.keyDates')}>
          <TableBody>
            {documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <p className={styles.empty_message}>{t('messages.emptyDocumentsMessage')}</p>
                </TableCell>
              </TableRow>
            )}
            {documents.map((doc) => (
              <DocumentsListRow
                key={`${doc.id}-${uuidv4()}`}
                doc={doc}
                renderActionButtons={renderActionButtons}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {fileTypeModalOpen && documentForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={(value) =>
            setFileTypeForDownloading(value as React.SetStateAction<'pdf' | 'doc'>)
          }
          handleFileNameChange={setFileNameForDownloading}
          document={documentForDownloading}
          handleDownloadContract={() => {
            if (fileTypeForDownloading === 'pdf') {
              setPdfSaveDialogOpen(true);
              setFileTypeModalOpen(false);
            } else {
              handleDownloadContract(documentForDownloading.id);
            }
          }}
          handleDownloadDocument={() =>
            downloadDocumentUploadItem(documentForDownloading.id, documentForDownloading.name ?? '')
          }
          handleDownloadHTML={
            documentForDownloading?.du_filetype &&
            documentForDownloading?.du_filetype.includes('pdf')
              ? () => {
                  setPdfSaveDialogOpen(true);
                  setFileTypeModalOpen(false);
                }
              : () => {
                  const documentName = documentForDownloading.name ?? '';
                  handleDownloadHTML(
                    (documentForDownloading?.document_upload_html?.length ?? 0) > 0
                      ? Math.max(...(documentForDownloading.document_upload_html ?? []))
                      : 0,
                    documentName.endsWith('.pdf') ||
                      documentName.endsWith('.docx') ||
                      documentName.endsWith('.doc')
                      ? documentName.replace('.pdf', '').replace('.docx', '').replace('.doc', '')
                      : documentName,
                    documentForDownloading.du_filetype?.[0] ?? ''
                  );
                }
          }
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('dialogTitles.deleteDocument')}
          modalMessage={t('messages.deleteDocumentDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          onDismiss={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          onClose={() => {
            handleDeleteDocument();
          }}
        />
      )}
      {pdfSaveDialogOpen && documentForDownloading && (
        <MessageModal
          open={pdfSaveDialogOpen}
          modalMessage={t('messages.chooseSaveAsPDF')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => {
            if (documentForDownloading?.html) {
              Helpers.convertHtmlToPdfAndDownload(
                documentForDownloading?.html,
                documentForDownloading?.name?.replace('.pdf', '')
              );
              setPdfSaveDialogOpen(false);
            } else {
              dispatch(showOverlay());
              NWClient.get<DocumentHtmlDataModel>(
                'document-upload-html-data',
                Math.max(...(documentForDownloading.document_upload_html ?? []))
              )
                .then((res) => {
                  Helpers.convertHtmlToPdfAndDownload(
                    res.html,
                    documentForDownloading?.name?.replace('.pdf', '')
                  );
                  setPdfSaveDialogOpen(false);
                  setDocumentForDownloading(null as unknown as Document);
                })
                .catch((error) => {
                  toast.error(getErrorMessage(error));
                })
                .finally(() => {
                  dispatch(hideOverlay());
                  setPdfSaveDialogOpen(false);
                });
            }
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setPdfSaveDialogOpen(false);
          }}
        />
      )}
      {documentDeletePendingStatus && <ProgressOverlay />}
    </>
  );
};

export default DocumentListVault;
